import { useState } from "react";
import { Link } from "react-router-dom";
import type { TestimonialDTO } from "../../shared/api";
import { VerificationBadge } from "./Badge";
import { isoDay } from "../lib/format";
import { personName } from "../../shared/people";

// Quotes on the home page are real reviews, not marketing copy. Everything
// shown here  the words, the reviewer, the listing, the verification badge
// is the same record the profile page renders, so a reader can click through
// and check it. A site arguing that reviews should be verifiable cannot put
// unverifiable praise on its own front page.

/** Initials for the avatar. No photos exist in the system, and inventing one
    would attach a face to a real person who never supplied it. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/**
 * What stands in the band before any verified review exists.
 *
 * The slot is not left empty and it is not filled with an invented person.
 * It explains the thing the quotes will eventually demonstrate, and shows the
 * two labels a reader will actually meet, so the band is doing real work in
 * the meantime rather than holding a space.
 *
 * It says nothing that stops being true once real quotes replace it.
 */
function ProofPromise() {
  return (
    <section className="testimonial-band" data-state="promise" aria-label="How reviews are labelled">
      <div className="container testimonial-inner">
        <div className="testimonial-figure">
          <p className="testimonial-quote">
            Every review here tells you whether the person could prove they used it.
          </p>
          <p className="promise-sub">
            Reviewers can sign a message from the wallet that held the token, or connect a
            read-only key from the exchange they traded on. Reviews that clear that bar are
            labelled. So are the ones that do not.
          </p>
          <Link to="/methodology" className="btn btn-primary btn-sm">
            How verification works →
          </Link>
        </div>

        <div className="promise-labels" aria-hidden>
          <div className="promise-label">
            <VerificationBadge level="wallet" />
            <span>Proved they held it or used it.</span>
          </div>
          <div className="promise-label">
            <VerificationBadge level="none" />
            <span>An opinion, weighted lower and never hidden.</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Testimonials({ items }: { items: TestimonialDTO[] }) {
  const [i, setI] = useState(0);

  // No verified reviews yet is a state this site will be in for a while. The
  // band explains the mechanism instead of inventing someone to praise it.
  if (items.length === 0) return <ProofPromise />;

  const t = items[Math.min(i, items.length - 1)]!;
  const who = personName(t.author, "Reviewer");

  return (
    <section className="testimonial-band" data-state="quotes" aria-label="What verified reviewers say">
      <div className="container testimonial-inner">
        <figure className="testimonial-figure">
          <blockquote className="testimonial-quote">
            <span aria-hidden className="testimonial-mark">&ldquo;</span>
            {t.quote}
          </blockquote>
          <figcaption className="testimonial-by">
            {t.author.username ? (
              <Link to={`/u/${t.author.username}`} className="testimonial-name">{who}</Link>
            ) : (
              <span className="testimonial-name">{who}</span>
            )}
            <span className="testimonial-meta">
              <VerificationBadge level={t.verificationLevel} />
            </span>
            <span className="testimonial-meta">
              Reviewed{" "}
              <Link to={`/${t.entity.type}/${t.entity.slug}`}>{t.entity.name}</Link>
              {" · "}
              <time dateTime={t.createdAt}>{isoDay(t.createdAt)}</time>
            </span>
          </figcaption>
        </figure>

        <div className="testimonial-avatar" aria-hidden>
          <span className="testimonial-ring" />
          <span className="testimonial-initials">{initials(who)}</span>
        </div>
      </div>

      <div className="container">
        {items.length > 1 && (
          <div className="testimonial-dots" role="tablist" aria-label="Choose a review">
            {items.map((item, n) => (
              <button
                key={item.id}
                role="tab"
                aria-selected={n === i}
                aria-label={`Review ${n + 1} of ${items.length}`}
                className={`testimonial-dot${n === i ? " is-on" : ""}`}
                onClick={() => setI(n)}
              />
            ))}
          </div>
        )}
        <p className="testimonial-note">
          Verified reviews rated 4 stars or better, ordered by how useful other readers found
          them. Every listing shows all of its reviews, critical ones included.
        </p>
      </div>
    </section>
  );
}
